<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import StoreSaleForm from './StoreSaleForm.vue'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { storeSalesApi, type StoreSale } from '@/services/storeSales'

/**
 * Edición de una venta de tienda.
 *
 * Carga la venta y la pasa al formulario compartido. El número de venta y la
 * facturación los conserva el servidor al guardar; aquí solo se editan los
 * datos y el valor.
 */

const route = useRoute()
const { volver: volverAlListado } = useVolverAlListado('store-sales.index')

const sale = ref<StoreSale | null>(null)
const loading = ref(true)
const errorMessage = ref<string | null>(null)

const saleId = Number.parseInt(String(route.params.id ?? ''), 10)

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await storeSalesApi.get(saleId)
    sale.value = data.data
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    errorMessage.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar la venta.'
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Editar venta de tienda
        </h1>
        <p
          v-if="sale !== null"
          class="mt-1 font-mono text-sm text-surface-500 dark:text-surface-400"
        >
          {{ sale.sale_number }}
        </p>
      </div>

      <Button
        label="Volver"
        icon="pi pi-arrow-left"
        severity="secondary"
        outlined
        @click="volverAlListado()"
      />
    </header>

    <div
      v-if="loading"
      class="space-y-3 rounded-xl border border-surface-200 bg-white p-6 dark:border-surface-700 dark:bg-surface-900"
    >
      <Skeleton
        v-for="linea in 6"
        :key="linea"
        height="1.5rem"
        class="w-full"
      />
    </div>

    <Message
      v-else-if="errorMessage !== null"
      severity="error"
      :closable="false"
      role="alert"
    >
      {{ errorMessage }}
    </Message>

    <StoreSaleForm
      v-else-if="sale !== null"
      :sale="sale"
    />
  </div>
</template>
